import React, { useEffect, useState } from 'react';
import { FiCheckCircle, FiMessageSquare, FiStar } from 'react-icons/fi';
import api from '../axios';

const stars = [1, 2, 3, 4, 5];

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({
    name: '',
    role: '',
    rating: 5,
    content: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await api.get('/api/reviews');
        setReviews(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Error fetching reviews:', err);
        setReviews([]);
      }
    };

    fetchReviews();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewReview((prev) => ({
      ...prev,
      [name]: name === 'rating' ? parseInt(value, 10) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitSuccess(false);
    setIsSubmitting(true);

    try {
      await api.post('/api/reviews', newReview);
      setNewReview({
        name: '',
        role: '',
        rating: 5,
        content: '',
      });
      setSubmitSuccess(true);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Please sign in before submitting a review.');
      } else {
        setError(err.response?.data?.detail || 'Failed to submit review.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden bg-[#07111a] text-white">
      <div className="fixed inset-0 -z-20 bg-[radial-gradient(circle_at_top_left,_rgba(27,186,123,0.16),_transparent_28%),radial-gradient(circle_at_80%_20%,_rgba(45,122,255,0.16),_transparent_30%),linear-gradient(180deg,_#09131d_0%,_#05080c_100%)]" />

      <section className="px-4 pb-16 pt-32 sm:px-6 lg:px-8 lg:pt-36">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-4 py-2 text-sm font-semibold text-emerald-200">
                <FiMessageSquare className="h-4 w-4" />
                Feedback from platform users
              </div>
              <h1 className="mt-6 max-w-4xl text-5xl font-black leading-[0.95] tracking-tight text-white sm:text-6xl lg:text-7xl">
                Reviews from users
                <span className="block bg-gradient-to-r from-emerald-300 via-cyan-300 to-sky-400 bg-clip-text text-transparent">
                  who shared their experience
                </span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
                Read feedback from users who have worked through onboarding, configuration, and day-to-day platform use. Signed-in users can also submit a review.
              </p>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl">
              <p className="text-sm uppercase tracking-[0.24em] text-cyan-200/70">What you will find here</p>
              <div className="mt-4 space-y-3 text-sm leading-7 text-slate-300">
                <p>Reviews focus on setup, usability, and the day-to-day experience of using the platform.</p>
                <p>Published feedback appears here once it has been reviewed.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-6 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl lg:p-8">
            <h2 className="text-3xl font-black text-white">Share your experience</h2>

            {submitSuccess && (
              <div className="mt-6 rounded-2xl border border-emerald-300/20 bg-emerald-300/10 p-4 text-sm text-emerald-200">
                Thank you. Your review was submitted and will appear once approved.
              </div>
            )}
            {error && (
              <div className="mt-6 rounded-2xl border border-rose-300/20 bg-rose-300/10 p-4 text-sm text-rose-200">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-8 space-y-5">
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-2 block text-sm font-medium text-slate-300">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={newReview.name}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-2xl border border-white/10 bg-slate-950/65 px-4 py-3 text-white outline-none transition focus:border-cyan-300/40"
                  />
                </div>
                <div>
                  <label htmlFor="role" className="mb-2 block text-sm font-medium text-slate-300">
                    Role
                  </label>
                  <input
                    type="text"
                    id="role"
                    name="role"
                    value={newReview.role}
                    onChange={handleInputChange}
                    className="w-full rounded-2xl border border-white/10 bg-slate-950/65 px-4 py-3 text-white outline-none transition focus:border-cyan-300/40"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-300">Rating</label>
                <div className="flex items-center gap-2">
                  {stars.map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setNewReview((prev) => ({ ...prev, rating: star }))}
                      className="rounded-xl p-1 text-yellow-300 transition hover:scale-110"
                    >
                      <FiStar className={`h-7 w-7 ${star <= newReview.rating ? 'fill-current' : ''}`} />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="content" className="mb-2 block text-sm font-medium text-slate-300">
                  Your Review
                </label>
                <textarea
                  id="content"
                  name="content"
                  rows="5"
                  value={newReview.content}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-2xl border border-white/10 bg-slate-950/65 px-4 py-3 text-white outline-none transition focus:border-cyan-300/40"
                  placeholder="Describe your experience with setup, controls, and overall usability."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-300 to-cyan-300 px-6 py-4 font-bold text-slate-950 transition hover:scale-[1.01] disabled:opacity-60"
              >
                {isSubmitting ? 'Submitting...' : 'Submit review'}
              </button>
            </form>
          </div>

          <div className="rounded-[2rem] border border-white/10 bg-white/5 p-6 backdrop-blur-xl lg:p-8">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-3xl font-black text-white">Approved reviews</h2>
              <div className="inline-flex items-center gap-2 text-sm text-slate-400">
                <FiCheckCircle className="h-4 w-4 text-emerald-300" />
                Public feedback only
              </div>
            </div>

            {reviews.length === 0 ? (
              <div className="mt-8 rounded-[1.75rem] border border-dashed border-white/12 bg-slate-950/55 p-8 text-center">
                <p className="text-lg font-semibold text-white">No approved reviews are visible yet.</p>
                <p className="mt-3 text-sm leading-7 text-slate-400">
                  Check back later or submit a review after spending some time with the platform.
                </p>
              </div>
            ) : (
              <div className="mt-8 grid gap-5 md:grid-cols-2">
                {reviews.map((review) => (
                  <div key={review.id || `${review.name}-${review.created_at}`} className="rounded-[1.75rem] border border-white/10 bg-slate-950/60 p-5">
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-300 to-cyan-300 font-black text-slate-950">
                        {(review.name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">{review.name}</h3>
                        <p className="text-sm text-slate-400">{review.role || 'Trader'}</p>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center gap-1 text-yellow-300">
                      {stars.map((star) => (
                        <FiStar key={`${review.id || review.name}-${star}`} className={`h-4 w-4 ${star <= review.rating ? 'fill-current' : ''}`} />
                      ))}
                    </div>

                    <p className="mt-4 text-sm leading-7 text-slate-300">{review.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
